import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import usePagination from '@mui/material/usePagination';
import './StudentCompletedSessionTable.css';
import { Avatar, Box, MenuItem, Select, Typography } from '@mui/material';
import { useState } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
// import dayjs from 'dayjs';

const SessionTypeColor = {
  one_on_one: { color: '#0A5EF3', bgcolor: '#E8F0FF', value: 'One-On-One' },
  group: { color: '#7BCF8C', bgcolor: '#FAFFFB', value: 'Group Sessions' },
  prime: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Prime Sessions' },
};

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

function formatText(input) {
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

export default function StudentCompletedSessionTable({
  completedSessionData,
  pagination,
  onPaginationChange,
  onSearchChange,
}) {
  const [filteredCompletedSessionData, setFilteredCompletedSessionData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (completedSessionData && Array.isArray(completedSessionData)) {
      setFilteredCompletedSessionData(completedSessionData);
    } else {
      setFilteredCompletedSessionData([]);
    }
  }, [completedSessionData]);

  const { items } = usePagination({
    count: Math.ceil((pagination?.total_count || 0) / (pagination?.page_size || 10)),
    page: pagination?.page || 1,
    onChange: (event, value) => {
      onPaginationChange(value, pagination?.page_size || 10);
    },
  });

  console.log('Completed pagination items:', items, 'pagination:', pagination);

  const handleCellClick = (event, id) => {
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      navigate(`/admin/users/learner/${completedSessionData[0]?.id}/session-details/${id}`);
    }
  };

  const handleRowsPerPageChange = event => {
    const newPageSize = event.target.value;
    onPaginationChange(1, newPageSize); // Reset to first page when changing page size
  };

  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';

    // Convert UTC to local time
    const start = dayjs(startTime).local();
    const end = start.add(lengthInMinutes, 'minute');

    // Different scenarios
    const startHour = start.format('h:mm');
    const endHour = end.format('h:mm');
    const startPeriod = start.format('A');
    const endPeriod = end.format('A');

    // Handling AM/PM transition
    if (startPeriod !== endPeriod) {
      return `${startHour} ${startPeriod} - ${endHour} ${endPeriod}`;
    }

    // Same period (AM or PM)
    return `${startHour} - ${end.format('h:mm A')}`;
  };

  // Convert UTC to local date format
  const formatDate = dateString => {
    if (!dateString) return '---';
    return dayjs(dateString).local().format('DD-MM-YYYY');
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="paginated table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                height: '25px',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              <TableCell>Instructor Name</TableCell>
              <TableCell align="left">Session Type</TableCell>
              {/* <TableCell align="left">Teacher Name</TableCell> */}
              <TableCell align="left">Date</TableCell>
              <TableCell align="left">Timing</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F) !important',
                height: '25px !important',
                fontFamily: 'Poppins !important',
                fontSize: '14px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: 'normal !important',
                padding: '9px 16px',
              },
            }}
          >
            {filteredCompletedSessionData?.length > 0 ? (
              filteredCompletedSessionData.map(row => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  sx={{
                    '& td': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#E9F1FF !important',
                      cursor: 'pointer',
                    },
                  }}
                >
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box className="flex items-center gap-4">
                      <Avatar
                        src={row?.teacher?.profile_image || '/broken-image.jpg'}
                        sx={{ width: '25px', height: '25px', padding: '2px' }}
                      />
                      <Typography>{row?.teacher?.name ? row?.teacher?.name : '---'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box
                      style={{
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '150px',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        border: `1px solid ${SessionTypeColor[row?.session_type]?.color}`,
                        backgroundColor: SessionTypeColor[row?.session_type]?.bgcolor,
                        color: SessionTypeColor[row?.session_type]?.color,
                        fontFamily: 'Poppins !important',
                        fontSize: '14px !important',
                        fontStyle: 'normal !important',
                        fontWeight: '400 !important',
                        lineHeight: 'normal !important',
                      }}
                    >
                      <Typography className="level-text">
                        {row?.session_type ? SessionTypeColor[row?.session_type]?.value : '---'}
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box className="flex items-center gap-4">
                      <Avatar
                        src={row?.teacher?.profile_image || '/broken-image.jpg'}
                        sx={{ width: '25px', height: '25px', padding: '2px' }}
                      />
                      <Typography>{row?.teacher?.name ? row?.teacher?.name : '---'}</Typography>
                    </Box>
                  </TableCell> */}
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatDate(row?.start_time)}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatTimeInterval(row?.start_time, row?.length_in_minutes)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'Poppins', fontSize: '16px' }}>
                    No completed session available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 16px 10px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Showing
          </Typography>
          <Select
            value={pagination?.page_size || 10}
            onChange={handleRowsPerPageChange}
            sx={{
              height: '30px',
              minWidth: '20px',
              fontFamily: 'Poppins',
              fontSize: '14px',
              '& .MuiSelect-select': {
                padding: '6px 4px 4px 8px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: '14px',
              color: '#666',
            }}
          >
            of {pagination?.total_count || 0} Sessions
          </Typography>
        </Box>
        <nav>
          <List>
            {items.map(({ page: itemPage, type, selected, ...item }, index) => {
              let children = null;

              if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                children = '…';
              } else if (type === 'page') {
                children = (
                  <button
                    type="button"
                    style={{
                      fontWeight: selected ? 'bold' : undefined,
                      margin: '0 5px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: selected ? '#000' : '#666',
                      backgroundColor: selected ? '#f0f0f0' : 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                    }}
                    {...item}
                  >
                    {itemPage}
                  </button>
                );
              } else {
                children = (
                  <button
                    type="button"
                    style={{
                      margin: '0 5px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: '#666',
                      border: 'none',
                      backgroundColor: 'transparent',
                    }}
                    {...item}
                  >
                    {formatText(type)}
                  </button>
                );
              }

              return <li key={index}>{children}</li>;
            })}
          </List>
        </nav>
      </Box>
    </>
  );
}
